import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  Car, LogOut, Plus, Search, Calendar, MapPin, User, Phone, 
  DollarSign, Clock, CheckCircle, XCircle, Truck,
  BarChart3, Users, TrendingUp, Settings, Menu, X, FileText, Wifi, WifiOff, Star, PieChart, Upload,
  Building, UserPlus, Mail, Edit, Percent, Filter, CalendarRange, ArrowUpDown, ArrowUp, ArrowDown, Trash2, Loader2,
  Send, RotateCcw
} from "lucide-react";
import { Sparkles } from "lucide-react";
import { ChatLeadsSection } from "../components/admin/ChatLeadsSection";
import { TourManagerSection } from "../components/admin/TourManagerSection";
import { motion, AnimatePresence } from "framer-motion";
import useWebSocket from "../hooks/useWebSocket";
import { BookingFilters, BulkActionBar, BookingCard } from "../components/admin";
import { DashboardSkeleton } from "../components/DashboardSkeleton";
import DispatchBoard from "../components/DispatchBoard";

// Helper function to format date and time in 24-hour format
const formatDateTime24 = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attentionFlags, setAttentionFlags] = useState([]);
  
  // Read initial tab from URL params or default to bookings
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || "dispatch");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  
  // Debounce search term for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Date filter state - persist using sessionStorage
  const [dateFrom, setDateFrom] = useState(() => sessionStorage.getItem('bookingDateFrom') || "");
  const [dateTo, setDateTo] = useState(() => sessionStorage.getItem('bookingDateTo') || "");
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('bookingStatusFilter') || "");
  const [sortColumn, setSortColumn] = useState(() => sessionStorage.getItem('bookingSortColumn') || "date");
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem('bookingSortDirection') || "asc");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Create User Dialog State
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    role: "client",
    company_name: "",
    is_corporate: false
  });

  // Corporate Client Management State
  const [corporateClients, setCorporateClients] = useState([]);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [showEditCorporateDialog, setShowEditCorporateDialog] = useState(false);
  const [selectedCorporate, setSelectedCorporate] = useState(null);
  const [newDiscount, setNewDiscount] = useState(0);
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [corporateFilter, setCorporateFilter] = useState("all");  // Filter bookings by corporate client
  const [showCorporateInvoiceDialog, setShowCorporateInvoiceDialog] = useState(false);
  const [editCorporateForm, setEditCorporateForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    abn: "",
    address: "",
    discount_percentage: 0,
    is_active: true
  });
  const [savingCorporate, setSavingCorporate] = useState(false);

  // Driver Edit State
  const [showEditDriverDialog, setShowEditDriverDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editDriverForm, setEditDriverForm] = useState({
    name: "",
    email: "",
    phone: "",
    abn: "",
    address: "",
    is_active: true
  });
  const [savingDriver, setSavingDriver] = useState(false);
  const [sendingEarningsEmail, setSendingEarningsEmail] = useState(false);

  // Reset Password Dialog State
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newEmail: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [resettingPassword, setResettingPassword] = useState(false);

  // Quotes State
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quoteStatusFilter, setQuoteStatusFilter] = useState("all");
  const [priceDialogQuote, setPriceDialogQuote] = useState(null);
  const [quotePriceInput, setQuotePriceInput] = useState("");
  const [sendingQuotePrice, setSendingQuotePrice] = useState(false);

  // Bulk Selection State
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // WebSocket for real-time updates
  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === "new_booking" || message.type === "booking_update" || 
        message.type === "booking_confirmed" || message.type === "booking_dispatched" ||
        message.type === "booking_status_update") {
      // Update bookings list
      setBookings(prev => {
        const index = prev.findIndex(b => b.id === message.data.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = message.data;
          return updated;
        } else {
          return [message.data, ...prev];
        }
      });
      
      // Show notification
      if (message.type === "new_booking") {
        toast.info(`New booking: ${message.data.booking_ref}`, {
          description: `${message.data.passenger_name} - ${message.data.pickup_location}`
        });
      }
    }
  }, []);

  const { isConnected } = useWebSocket(handleWebSocketMessage);

  useEffect(() => {
    // Always fetch data on load - filters will be applied client-side
    fetchData();
  }, []);

  const fetchFilteredBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo + "T23:59:59");
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await api.get(`/bookings?${params.toString()}`);
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to filter bookings");
    }
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    sessionStorage.removeItem('bookingDateFrom');
    sessionStorage.removeItem('bookingDateTo');
    sessionStorage.removeItem('bookingStatusFilter');
    fetchData();
  };

  // Persist filters to sessionStorage when they change
  useEffect(() => {
    if (dateFrom) sessionStorage.setItem('bookingDateFrom', dateFrom);
    else sessionStorage.removeItem('bookingDateFrom');
  }, [dateFrom]);

  useEffect(() => {
    if (dateTo) sessionStorage.setItem('bookingDateTo', dateTo);
    else sessionStorage.removeItem('bookingDateTo');
  }, [dateTo]);

  useEffect(() => {
    if (statusFilter) sessionStorage.setItem('bookingStatusFilter', statusFilter);
    else sessionStorage.removeItem('bookingStatusFilter');
  }, [statusFilter]);

  useEffect(() => {
    sessionStorage.setItem('bookingSortColumn', sortColumn);
  }, [sortColumn]);

  useEffect(() => {
    sessionStorage.setItem('bookingSortDirection', sortDirection);
  }, [sortDirection]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, bookingsRes, usersRes, driversRes, corporateRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/bookings"),
        api.get("/users"),
        api.get("/users/drivers"),
        api.get("/corporate-clients")
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setUsers(usersRes.data);
      setDrivers(driversRes.data);
      setCorporateClients(corporateRes.data);
      
      // Fetch quotes and attention flags
      fetchQuotes();
      fetchAttentionFlags();
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const quotesFetchId = useRef(0);
  const fetchQuotes = async () => {
    const fetchId = ++quotesFetchId.current;
    setQuotesLoading(true);
    try {
      const params = quoteStatusFilter && quoteStatusFilter !== "all" 
        ? `?status=${quoteStatusFilter}` 
        : "";
      const response = await api.get(`/quotes${params}`);
      if (fetchId !== quotesFetchId.current) return;
      setQuotes(response.data);
    } catch (error) {
      console.error("Failed to fetch quotes:", error);
    } finally {
      if (fetchId === quotesFetchId.current) setQuotesLoading(false);
    }
  };

  const fetchAttentionFlags = async () => {
    try {
      const response = await api.get("/bookings/dispatch/attention-flags");
      setAttentionFlags(response.data);
    } catch (error) {
      console.error("Failed to fetch attention flags:", error);
    }
  };

  const handleResendQuote = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/resend`);
      toast.success("Quote email resent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend quote");
    }
  };

  const handleSendQuotePrice = async () => {
    const price = parseFloat(quotePriceInput);
    if (!price || price <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    setSendingQuotePrice(true);
    try {
      await api.post(`/quotes/${priceDialogQuote.id}/send-price`, { price });
      toast.success("Quote priced and emailed to customer");
      setPriceDialogQuote(null);
      setQuotePriceInput("");
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send quote");
    } finally {
      setSendingQuotePrice(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const openDiscountDialog = (client) => {
    setSelectedCorporate(client);
    setNewDiscount(client.discount_percentage || 0);
    setShowDiscountDialog(true);
  };

  const handleSaveDiscount = async () => {
    if (!selectedCorporate) return;
    
    setSavingDiscount(true);
    try {
      await api.put(`/corporate-clients/${selectedCorporate.id}/discount`, {
        discount_percentage: newDiscount
      });
      toast.success(`Discount updated to ${newDiscount}% for ${selectedCorporate.company_name || selectedCorporate.name}`);
      setShowDiscountDialog(false);
      // Refresh corporate clients
      const corporateRes = await api.get("/corporate-clients");
      setCorporateClients(corporateRes.data);
    } catch (error) {
      toast.error("Failed to update discount");
    } finally {
      setSavingDiscount(false);
    }
  };

  const openEditCorporateDialog = (client) => {
    setSelectedCorporate(client);
    setEditCorporateForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      company_name: client.company_name || "",
      abn: client.abn || "",
      address: client.address || "",
      discount_percentage: client.discount_percentage || 0,
      is_active: client.is_active !== false
    });
    setShowEditCorporateDialog(true);
  };

  const handleSaveCorporate = async () => {
    if (!selectedCorporate) return;
    
    setSavingCorporate(true);
    try {
      await api.put(`/corporate-clients/${selectedCorporate.id}`, editCorporateForm);
      toast.success(`Corporate account updated successfully`);
      setShowEditCorporateDialog(false);
      // Refresh corporate clients
      const corporateRes = await api.get("/corporate-clients");
      setCorporateClients(corporateRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update corporate account");
    } finally {
      setSavingCorporate(false);
    }
  };

  const handleDeleteCorporate = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.company_name || client.name}"? If they have bookings, the account will be deactivated instead.`)) return;
    
    try {
      const response = await api.delete(`/corporate-clients/${client.id}`);
      toast.success(response.data.message);
      // Refresh corporate clients
      const corporateRes = await api.get("/corporate-clients");
      setCorporateClients(corporateRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete corporate account");
    }
  };

  const openEditDriverDialog = async (driver) => {
    setSelectedDriver(driver);
    setEditDriverForm({
      name: driver.name || "",
      email: driver.email || "",
      phone: driver.phone || "",
      abn: driver.abn || "",
      address: driver.address || "",
      is_active: driver.is_active !== false
    });
    setShowEditDriverDialog(true);
  };

  const handleSaveDriver = async () => {
    if (!selectedDriver) return;
    
    setSavingDriver(true);
    try {
      await api.put(`/drivers/${selectedDriver.id}`, editDriverForm);
      toast.success("Driver updated successfully");
      setShowEditDriverDialog(false);
      // Refresh drivers
      const driversRes = await api.get("/users/drivers");
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update driver");
    } finally {
      setSavingDriver(false);
    }
  };

  const handleSendEarningsEmail = async (driverId, driverName) => {
    setSendingEarningsEmail(true);
    try {
      const response = await api.post(`/drivers/${driverId}/send-earnings-email`);
      toast.success(`Earnings email sent to ${driverName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send earnings email");
    } finally {
      setSendingEarningsEmail(false);
    }
  };

  const handleSendAllEarningsEmails = async () => {
    setSendingEarningsEmail(true);
    try {
      const response = await api.post("/drivers/send-all-earnings-emails");
      toast.success(`Earnings emails sent to ${response.data.drivers_with_activity} driver(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send earnings emails");
    } finally {
      setSendingEarningsEmail(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUserForm.email || !newUserForm.password || !newUserForm.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (newUserForm.is_corporate && !newUserForm.company_name) {
      toast.error("Please enter company name for corporate account");
      return;
    }

    setCreatingUser(true);
    try {
      await api.post("/admin/users", newUserForm);
      toast.success(`${newUserForm.is_corporate ? "Corporate" : "Client"} account created successfully`);
      setShowCreateUserDialog(false);
      setNewUserForm({
        email: "",
        password: "",
        name: "",
        phone: "",
        role: "client",
        company_name: "",
        is_corporate: false
      });
      fetchData(); // Refresh users list
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create user";
      toast.error(msg);
    } finally {
      setCreatingUser(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      dispatched: "status-dispatched",
      in_progress: "status-in_progress",
      completed: "status-completed",
      cancelled: "status-cancelled"
    };
    return <Badge className={`${styles[status]} capitalize`}>{status.replace("_", " ")}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: "payment-pending",
      paid: "payment-paid",
      failed: "payment-failed"
    };
    return <Badge className={`${styles[status]} capitalize`}>{status}</Badge>;
  };

  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
      const matchesSearch = 
        booking.booking_ref?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        booking.passenger_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        booking.pickup_location?.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      const matchesStatus = !statusFilter || statusFilter === "all" || booking.status === statusFilter;
      const matchesCorporate = !corporateFilter || corporateFilter === "all" || booking.corporate_client_id === corporateFilter;
      return matchesSearch && matchesStatus && matchesCorporate;
    }).sort((a, b) => {
      let comparison = 0;
      
      switch (sortColumn) {
        case "date":
          comparison = new Date(a.pickup_datetime) - new Date(b.pickup_datetime);
          break;
        case "status":
          comparison = (a.status || "").localeCompare(b.status || "");
          break;
        case "vehicle":
          comparison = (a.vehicle_type || "").localeCompare(b.vehicle_type || "");
          break;
        case "passenger":
          comparison = (a.passenger_name || "").localeCompare(b.passenger_name || "");
          break;
        case "reference":
          comparison = (a.booking_ref || "").localeCompare(b.booking_ref || "");
          break;
        default:
          comparison = new Date(a.pickup_datetime) - new Date(b.pickup_datetime);
      }
      
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [bookings, debouncedSearchTerm, statusFilter, corporateFilter, sortColumn, sortDirection]);

  // Bulk Selection Handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedBookings(filteredBookings.map(b => b.id));
    } else {
      setSelectedBookings([]);
    }
  };

  const handleSelectBooking = (bookingId, checked) => {
    if (checked) {
      setSelectedBookings(prev => [...prev, bookingId]);
    } else {
      setSelectedBookings(prev => prev.filter(id => id !== bookingId));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBookings.length === 0) return;
    setDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    setDeleteConfirmOpen(false);
    setBulkActionLoading(true);
    try {
      await Promise.all(selectedBookings.map(id => api.delete(`/bookings/${id}`)));
      toast.success(`${selectedBookings.length} booking(s) deleted`);
      setSelectedBookings([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete some bookings");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDispatch = async (driverId) => {
    if (selectedBookings.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedBookings.map(id => 
          api.post(`/bookings/${id}/dispatch`, { driver_id: driverId, send_to_calendar: false })
        )
      );
      toast.success(`${selectedBookings.length} booking(s) dispatched`);
      setSelectedBookings([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to dispatch some bookings");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleConfirmBooking = useCallback(async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/confirm`);
      toast.success("Booking confirmed");
      fetchData();
    } catch (error) {
      toast.error("Failed to confirm booking");
    }
  }, []);

  const handleDispatchBooking = useCallback(async (bookingId, driverId) => {
    try {
      await api.post(`/bookings/${bookingId}/dispatch`, { driver_id: driverId, send_to_calendar: false });
      toast.success("Booking dispatched to driver");
      fetchData();
    } catch (error) {
      toast.error("Failed to dispatch booking");
    }
  }, []);

  const toggleUserStatus = useCallback(async (userId) => {
    try {
      await api.put(`/users/${userId}/toggle-status`);
      toast.success("User status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  }, []);

  const handleDeleteUser = useCallback(async (targetUser) => {
    if (!window.confirm(`Are you sure you want to delete "${targetUser.name}"? If they have associated bookings, the account will be deactivated instead.`)) return;
    
    try {
      const response = await api.delete(`/users/${targetUser.id}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  }, []);

  // Open reset password dialog
  const openResetPasswordDialog = (userToReset) => {
    setSelectedUserForReset(userToReset);
    setResetPasswordForm({
      newEmail: userToReset.email,
      newPassword: "",
      confirmPassword: ""
    });
    setShowResetPasswordDialog(true);
  };

  // Handle reset password submission
  const handleResetPassword = async () => {
    if (!resetPasswordForm.newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (resetPasswordForm.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setResettingPassword(true);
    try {
      const payload = {
        user_id: selectedUserForReset.id,
        new_password: resetPasswordForm.newPassword
      };
      
      // Only include new email if it's different
      if (resetPasswordForm.newEmail !== selectedUserForReset.email) {
        payload.new_email = resetPasswordForm.newEmail;
      }

      await api.post("/auth/admin/reset-password", payload);
      toast.success(`Login credentials updated for ${selectedUserForReset.name}`);
      setShowResetPasswordDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const navItems = [
    { id: "dispatch", label: "Dispatch", icon: Truck },
    { id: "bookings", label: "Bookings", icon: Calendar },
    { id: "quotes", label: "Quotes", icon: Send },
    { id: "chatleads", label: "Chat Leads", icon: Sparkles },
    { id: "tours", label: "Tours", icon: MapPin },
    { id: "users", label: "Users", icon: Users },
    { id: "drivers", label: "Drivers", icon: Truck },
    { id: "corporate", label: "Corporate", icon: Building },
    { id: "reports", label: "Reports", icon: PieChart, link: "/reports" },
    { id: "ratings", label: "Ratings", icon: Star, link: "/ratings" },
    { id: "payroll", label: "Payroll", icon: DollarSign, link: "/payroll" },
    { id: "extras", label: "Extras", icon: DollarSign, link: "/extras" },
    { id: "bulk", label: "Bulk Import", icon: Upload, link: "/bulk-booking" },
    { id: "invoices", label: "Invoices", icon: FileText, link: "/invoices" },
    { id: "settings", label: "Settings", icon: Settings, link: "/settings" }
  ];

  const tabContentVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] } },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };

  if (loading) {
    return <DashboardSkeleton role="admin" />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#111114] border-b border-white/[0.06]">
        <div className="flex items-center justify-between p-4">
          <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-8 w-auto" />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white/70">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[#111114]/98 backdrop-blur-lg pt-16">
          <nav className="p-4 space-y-1">
            {navItems.map(item => (
              item.link ? (
                <Link
                  key={item.id}
                  to={item.link}
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-white/50 hover:bg-white/[0.04] hover:text-white/80"
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === item.id ? "bg-[#C8A95B]/10 text-[#C8A95B]" : "text-white/50 hover:bg-white/[0.04] hover:text-white/80"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </button>
              )
            ))}
            <hr className="border-white/[0.06] my-4" />
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </nav>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar — Premium Dark */}
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-[#111114] border-r border-white/[0.06] fixed">
          <div className="p-6 pb-4">
            <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
          </div>
          
          <nav className="flex-1 px-3 space-y-0.5">
            {navItems.map(item => {
              const isActive = !item.link && activeTab === item.id;
              const baseClasses = "w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm";
              const activeClasses = "bg-[#C8A95B]/10 text-[#C8A95B] font-medium";
              const inactiveClasses = "text-white/45 hover:bg-white/[0.04] hover:text-white/75";
              
              return item.link ? (
                <Link
                  key={item.id}
                  to={item.link}
                  className={`${baseClasses} ${inactiveClasses}`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="h-[18px] w-[18px]" />
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="h-[18px] w-[18px]" />
                  {item.label}
                  {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#C8A95B]" />}
                </button>
              );
            })}
          </nav>

          <div className="p-3 border-t border-white/[0.06]">
            {/* WebSocket Status */}
            <div className="flex items-center gap-2 px-3 py-2 mb-1">
              {isConnected ? (
                <>
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[11px] text-green-500/80">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3.5 w-3.5 text-white/25" />
                  <span className="text-[11px] text-white/25">Reconnecting...</span>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-3 px-3 py-2 mb-1 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#C8A95B]/15 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-[#C8A95B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white/80 truncate">{user?.name}</p>
                <p className="text-[11px] text-white/30 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
          <div className="p-4 lg:p-8">
            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
            >
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold">Admin Dashboard</h1>
                <p className="text-muted-foreground">Manage your transfer business</p>
              </div>
              <Link to="/booking/new">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="new-booking-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Booking
                </Button>
              </Link>
            </motion.div>

            <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              variants={tabContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
            {/* Dispatch Tab */}
            {activeTab === "dispatch" && (
              <DispatchBoard
                bookings={bookings}
                drivers={drivers}
                attentionFlags={attentionFlags}
                onRefresh={fetchData}
              />
            )}

            {/* Bookings Tab */}
            {activeTab === "bookings" && (
              <div className="space-y-6">
                {/* Filters - Using BookingFilters Component */}
                <BookingFilters
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  corporateFilter={corporateFilter}
                  setCorporateFilter={setCorporateFilter}
                  corporateClients={corporateClients}
                  sortColumn={sortColumn}
                  setSortColumn={setSortColumn}
                  sortDirection={sortDirection}
                  setSortDirection={setSortDirection}
                  dateFrom={dateFrom}
                  setDateFrom={setDateFrom}
                  dateTo={dateTo}
                  setDateTo={setDateTo}
                  onApplyFilters={fetchFilteredBookings}
                  onClearFilters={clearFilters}
                  filteredBookingsCount={filteredBookings.length}
                />

                {/* Bulk Actions Bar - Using BulkActionBar Component */}
                <BulkActionBar
                  selectedCount={selectedBookings.length}
                  drivers={drivers}
                  onDispatch={handleBulkDispatch}
                  onDelete={handleBulkDelete}
                  onClear={() => setSelectedBookings([])}
                  loading={bulkActionLoading}
                />
                
                {/* Bookings List - Desktop Table */}
                <Card className="dashboard-card hidden md:block">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th className="w-10">
                              <Checkbox
                                checked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0}
                                onCheckedChange={handleSelectAll}
                                data-testid="select-all-checkbox"
                              />
                            </th>
                            <th>Reference</th>
                            <th>Passenger</th>
                            <th>Route</th>
                            <th>Date/Time</th>
                            <th>Vehicle</th>
                            <th>Driver</th>
                            <th>Status</th>
                            <th>Payment</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBookings.map(booking => (
                            <tr key={booking.id} className={selectedBookings.includes(booking.id) ? "bg-primary/5" : ""}>
                              <td>
                                <Checkbox
                                  checked={selectedBookings.includes(booking.id)}
                                  onCheckedChange={(checked) => handleSelectBooking(booking.id, checked)}
                                  data-testid={`select-${booking.id}`}
                                />
                              </td>
                              <td className="mono text-primary cursor-pointer hover:underline" onClick={() => navigate(`/booking/${booking.id}`)}>{booking.booking_ref}</td>
                              <td>
                                <div>
                                  <p className="font-medium">{booking.passenger_name}</p>
                                  <p className="text-xs text-muted-foreground">{booking.passenger_phone}</p>
                                </div>
                              </td>
                              <td>
                                <div className="max-w-[200px]">
                                  <p className="text-sm truncate">{booking.pickup_location}</p>
                                  <p className="text-xs text-muted-foreground truncate">→ {booking.dropoff_location}</p>
                                </div>
                              </td>
                              <td className="mono text-sm whitespace-nowrap">{formatDateTime24(booking.pickup_datetime)}</td>
                              <td className="capitalize">{booking.vehicle_type}</td>
                              <td>{booking.driver_name || <span className="text-muted-foreground">Unassigned</span>}</td>
                              <td>{getStatusBadge(booking.status)}</td>
                              <td>{getPaymentBadge(booking.payment_status)}</td>
                              <td>
                                <div className="flex gap-2">
                                  {booking.status === "pending" && (
                                    <Button size="sm" variant="outline" onClick={() => handleConfirmBooking(booking.id)} data-testid={`confirm-${booking.id}`}>
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {(booking.status === "confirmed" || booking.status === "pending") && !booking.driver_id && drivers.length > 0 && (
                                    <Select onValueChange={(driverId) => handleDispatchBooking(booking.id, driverId)}>
                                      <SelectTrigger className="w-32 h-8" data-testid={`dispatch-${booking.id}`}>
                                        <SelectValue placeholder="Dispatch" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {drivers.map(driver => (
                                          <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredBookings.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                          No bookings found
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Bookings List - Mobile Cards - Using BookingCard Component */}
                <div className="md:hidden space-y-3">
                  {filteredBookings.length === 0 ? (
                    <Card className="dashboard-card">
                      <CardContent className="py-12 text-center text-muted-foreground">
                        No bookings found
                      </CardContent>
                    </Card>
                  ) : (
                    filteredBookings.map(booking => (
                      <BookingCard
                        key={booking.id}
                        booking={booking}
                        isSelected={selectedBookings.includes(booking.id)}
                        onSelect={handleSelectBooking}
                        onConfirm={handleConfirmBooking}
                        onDispatch={handleDispatchBooking}
                        drivers={drivers}
                      />
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === "users" && (
              <Card className="dashboard-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>All Users</CardTitle>
                  <Button onClick={() => setShowCreateUserDialog(true)} data-testid="create-user-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create User
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Role</th>
                          <th>Company</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.id}>
                            <td className="font-medium">{u.name}</td>
                            <td className="mono text-sm">{u.email}</td>
                            <td className="mono text-sm">{u.phone || "-"}</td>
                            <td>
                              <Badge variant="outline" className="capitalize">
                                {u.company_name ? "Corporate" : u.role}
                              </Badge>
                            </td>
                            <td>{u.company_name || "-"}</td>
                            <td>
                              <Badge className={u.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                                {u.is_active ? "Active" : "Disabled"}
                              </Badge>
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openResetPasswordDialog(u)}
                                  className="text-primary"
                                  data-testid={`reset-password-${u.id}`}
                                >
                                  <Settings className="h-4 w-4 mr-1" />
                                  Reset Login
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => toggleUserStatus(u.id)}
                                  className={u.is_active ? "text-destructive" : "text-green-400"}
                                  data-testid={`toggle-user-${u.id}`}
                                >
                                  {u.is_active ? "Disable" : "Enable"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteUser(u)}
                                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Drivers Tab */}
            {activeTab === "drivers" && (
              <Card className="dashboard-card" data-testid="drivers-tab">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-primary" />
                    Drivers
                  </CardTitle>
                  <Button 
                    onClick={handleSendAllEarningsEmails}
                    disabled={sendingEarningsEmail}
                    variant="outline"
                    data-testid="send-all-earnings-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {sendingEarningsEmail ? "Sending..." : "Send All Earnings Emails"}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {drivers.map(driver => (
                      <div 
                        key={driver.id} 
                        className="p-4 rounded-xl bg-secondary/30 border border-white/5 hover:border-primary/30 transition-colors"
                        data-testid={`driver-card-${driver.id}`}
                      >
                        <div 
                          className="flex items-center gap-4 cursor-pointer"
                          onClick={() => openEditDriverDialog(driver)}
                        >
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium hover:text-primary transition-colors">{driver.name}</p>
                            <p className="text-sm text-muted-foreground mono">{driver.phone || driver.email}</p>
                          </div>
                          <Edit className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="mt-3 flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDriverDialog(driver);
                            }}
                            data-testid={`edit-driver-${driver.id}`}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendEarningsEmail(driver.id, driver.name);
                            }}
                            disabled={sendingEarningsEmail}
                            data-testid={`send-earnings-${driver.id}`}
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            Earnings
                          </Button>
                        </div>
                      </div>
                    ))}
                    {drivers.length === 0 && (
                      <div className="col-span-full text-center py-8 text-muted-foreground">
                        No drivers found
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Corporate Tab */}
            {activeTab === "corporate" && (
              <Card className="dashboard-card" data-testid="corporate-tab">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-primary" />
                    Corporate Accounts
                  </CardTitle>
                  <Button 
                    onClick={() => setShowCreateUserDialog(true)}
                    data-testid="create-corporate-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Corporate
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {corporateClients.filter(c => c.company_name).map(client => (
                      <div 
                        key={client.id} 
                        className="p-4 rounded-xl bg-secondary/30 border border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4"
                        data-testid={`corporate-client-${client.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                            <Building className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{client.company_name}</p>
                            <p className="text-sm text-muted-foreground">{client.name} • {client.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">DISCOUNT</p>
                            <p className={`text-lg font-bold ${client.discount_percentage > 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                              {client.discount_percentage || 0}%
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openEditCorporateDialog(client)}
                            data-testid={`edit-corporate-${client.id}`}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openDiscountDialog(client)}
                            data-testid={`edit-discount-${client.id}`}
                          >
                            <Percent className="h-4 w-4 mr-2" />
                            Discount
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteCorporate(client)}
                            data-testid={`delete-corporate-${client.id}`}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {corporateClients.filter(c => c.company_name).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No corporate accounts yet. Create one to get started.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quotes Tab */}
            {activeTab === "chatleads" && <ChatLeadsSection />}

            {activeTab === "tours" && <TourManagerSection />}

            {activeTab === "quotes" && (
              <Card className="dashboard-card" data-testid="quotes-tab">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-primary" />
                    Email Quotes
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select value={quoteStatusFilter} onValueChange={(v) => { setQuoteStatusFilter(v); }}>
                      <SelectTrigger className="w-40 bg-secondary/50 border-black/10">
                        <SelectValue placeholder="Filter status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="requested">Requested</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={fetchQuotes}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Link to="/booking/new">
                      <Button data-testid="new-quote-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        New Quote
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {quotesLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : quotes.length > 0 ? (
                    <div className="space-y-3">
                      {quotes.filter(q => quoteStatusFilter === "all" || q.status === quoteStatusFilter).map(quote => (
                        <div 
                          key={quote.id}
                          className="p-4 rounded-xl bg-secondary/30 border border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4"
                          data-testid={`quote-${quote.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              quote.status === 'pending' ? 'bg-yellow-500/20' :
                              quote.status === 'accepted' ? 'bg-blue-500/20' :
                              quote.status === 'paid' ? 'bg-green-500/20' :
                              quote.status === 'declined' ? 'bg-red-500/20' :
                              'bg-gray-500/20'
                            }`}>
                              <Send className={`h-6 w-6 ${
                                quote.status === 'pending' ? 'text-yellow-500' :
                                quote.status === 'accepted' ? 'text-blue-500' :
                                quote.status === 'paid' ? 'text-green-500' :
                                quote.status === 'declined' ? 'text-red-500' :
                                'text-gray-500'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">{quote.quote_ref}</p>
                              <p className="text-sm text-muted-foreground">{quote.passenger_name}</p>
                              <p className="text-xs text-muted-foreground">{quote.passenger_email}</p>
                            </div>
                          </div>
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate max-w-[200px]">{quote.pickup_location}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>{new Date(quote.pickup_datetime).toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              {quote.status === 'requested' ? (
                                <p className="text-sm font-semibold text-purple-600">Awaiting price</p>
                              ) : (
                                <p className="text-lg font-bold text-primary">${quote.price?.toFixed(2)}</p>
                              )}
                              <Badge className={`${
                                quote.status === 'requested' ? 'bg-purple-500/20 text-purple-600' :
                                quote.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                                quote.status === 'accepted' ? 'bg-blue-500/20 text-blue-500' :
                                quote.status === 'paid' ? 'bg-green-500/20 text-green-500' :
                                quote.status === 'declined' ? 'bg-red-500/20 text-red-500' :
                                'bg-gray-500/20 text-gray-500'
                              }`}>
                                {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                              </Badge>
                            </div>
                            {quote.status === 'requested' && (
                              <Button
                                size="sm"
                                onClick={() => { setPriceDialogQuote(quote); setQuotePriceInput(""); }}
                                data-testid={`price-quote-${quote.id}`}
                              >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Set Price & Send
                              </Button>
                            )}
                            {quote.status === 'pending' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleResendQuote(quote.id)}
                                data-testid={`resend-quote-${quote.id}`}
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                Resend
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      No quotes found. Create a new quote from the booking form.
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* Quote Price Dialog */}
      <Dialog open={!!priceDialogQuote} onOpenChange={(open) => !open && setPriceDialogQuote(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Price Quote {priceDialogQuote?.quote_ref}
            </DialogTitle>
          </DialogHeader>
          {priceDialogQuote && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-secondary/30 border border-white/5 text-sm space-y-1">
                <p className="font-medium">{priceDialogQuote.passenger_name} · {priceDialogQuote.num_passengers} pax · {priceDialogQuote.vehicle_type}</p>
                <p className="text-muted-foreground">{priceDialogQuote.pickup_location} → {priceDialogQuote.dropoff_location}</p>
                <p className="text-muted-foreground">{new Date(priceDialogQuote.pickup_datetime).toLocaleString()}</p>
                {priceDialogQuote.distance_km && <p className="text-muted-foreground">~{priceDialogQuote.distance_km} km</p>}
                {priceDialogQuote.special_requirements && <p className="text-muted-foreground">Notes: {priceDialogQuote.special_requirements}</p>}
              </div>
              <div className="space-y-2">
                <Label>Quote Price (AUD, inc. GST)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 250"
                  value={quotePriceInput}
                  onChange={(e) => setQuotePriceInput(e.target.value)}
                  data-testid="quote-price-input"
                />
              </div>
              <Button className="w-full" onClick={handleSendQuotePrice} disabled={sendingQuotePrice} data-testid="send-quote-price-btn">
                {sendingQuotePrice ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send Quote to Customer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Set Corporate Discount
            </DialogTitle>
          </DialogHeader>
          {selectedCorporate && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-secondary/30 border border-white/5">
                <p className="font-medium">{selectedCorporate.company_name}</p>
                <p className="text-sm text-muted-foreground">{selectedCorporate.email}</p>
              </div>
              <div className="space-y-2">
                <Label>Discount Percentage (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={newDiscount}
                  onChange={(e) => setNewDiscount(parseFloat(e.target.value) || 0)}
                  data-testid="discount-input"
                />
                <p className="text-xs text-muted-foreground">
                  This discount will be automatically applied to all bookings for this corporate client.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDiscountDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveDiscount} disabled={savingDiscount} data-testid="save-discount-btn">
                  {savingDiscount ? "Saving..." : "Save Discount"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Corporate Dialog */}
      <Dialog open={showEditCorporateDialog} onOpenChange={setShowEditCorporateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-primary" />
              Edit Corporate Account
            </DialogTitle>
          </DialogHeader>
          {selectedCorporate && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={editCorporateForm.company_name}
                  onChange={(e) => setEditCorporateForm(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="ABC Corporation Pty Ltd"
                  data-testid="edit-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person Name *</Label>
                <Input
                  value={editCorporateForm.name}
                  onChange={(e) => setEditCorporateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="edit-contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={editCorporateForm.email}
                  onChange={(e) => setEditCorporateForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  data-testid="edit-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={editCorporateForm.phone}
                  onChange={(e) => setEditCorporateForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+61 400 000 000"
                  data-testid="edit-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>ABN (optional)</Label>
                <Input
                  value={editCorporateForm.abn || ""}
                  onChange={(e) => setEditCorporateForm(prev => ({ ...prev, abn: e.target.value }))}
                  placeholder="XX XXX XXX XXX"
                  data-testid="edit-abn"
                />
              </div>
              <div className="space-y-2">
                <Label>Business Address (optional)</Label>
                <Input
                  value={editCorporateForm.address || ""}
                  onChange={(e) => setEditCorporateForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Business St, Sydney NSW 2000"
                  data-testid="edit-address"
                />
              </div>
              <div className="space-y-2">
                <Label>Discount Percentage (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={editCorporateForm.discount_percentage}
                  onChange={(e) => setEditCorporateForm(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))}
                  data-testid="edit-discount"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/5 border border-black/10">
                <div>
                  <p className="font-medium">Active Account</p>
                  <p className="text-xs text-muted-foreground">Disable to prevent login</p>
                </div>
                <Switch
                  checked={editCorporateForm.is_active}
                  onCheckedChange={(checked) => setEditCorporateForm(prev => ({ ...prev, is_active: checked }))}
                  data-testid="edit-active-toggle"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditCorporateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveCorporate} disabled={savingCorporate} data-testid="save-corporate-btn">
                  {savingCorporate ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={showEditDriverDialog} onOpenChange={setShowEditDriverDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              Edit Driver
            </DialogTitle>
          </DialogHeader>
          {selectedDriver && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Driver Name *</Label>
                <Input
                  value={editDriverForm.name}
                  onChange={(e) => setEditDriverForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="edit-driver-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={editDriverForm.email}
                  onChange={(e) => setEditDriverForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="driver@example.com"
                  data-testid="edit-driver-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={editDriverForm.phone}
                  onChange={(e) => setEditDriverForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+61 400 000 000"
                  data-testid="edit-driver-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>ABN (optional)</Label>
                <Input
                  value={editDriverForm.abn || ""}
                  onChange={(e) => setEditDriverForm(prev => ({ ...prev, abn: e.target.value }))}
                  placeholder="XX XXX XXX XXX"
                  data-testid="edit-driver-abn"
                />
              </div>
              <div className="space-y-2">
                <Label>Address (optional)</Label>
                <Input
                  value={editDriverForm.address || ""}
                  onChange={(e) => setEditDriverForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Driver St, Sydney NSW 2000"
                  data-testid="edit-driver-address"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/5 border border-black/10">
                <div>
                  <p className="font-medium">Active Driver</p>
                  <p className="text-xs text-muted-foreground">Disable to prevent login and job assignments</p>
                </div>
                <Switch
                  checked={editDriverForm.is_active}
                  onCheckedChange={(checked) => setEditDriverForm(prev => ({ ...prev, is_active: checked }))}
                  data-testid="edit-driver-active-toggle"
                />
              </div>
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Earnings Email</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Send a weekly earnings summary to this driver
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleSendEarningsEmail(selectedDriver.id, selectedDriver.name)}
                  disabled={sendingEarningsEmail}
                  data-testid="send-driver-earnings-btn"
                >
                  <Mail className="h-3 w-3 mr-2" />
                  {sendingEarningsEmail ? "Sending..." : "Send Earnings Email"}
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditDriverDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveDriver} disabled={savingDriver} data-testid="save-driver-btn">
                  {savingDriver ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Create New User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            {/* Account Type Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-black/5 border border-black/10">
              <div className="flex items-center gap-3">
                <Building className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">Corporate Account</p>
                  <p className="text-xs text-muted-foreground">Enable for business clients</p>
                </div>
              </div>
              <Switch
                checked={newUserForm.is_corporate}
                onCheckedChange={(checked) => setNewUserForm(prev => ({ 
                  ...prev, 
                  is_corporate: checked,
                  role: "client"
                }))}
                data-testid="corporate-toggle"
              />
            </div>

            {/* Corporate Company Name */}
            {newUserForm.is_corporate && (
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={newUserForm.company_name}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="ABC Corporation Pty Ltd"
                  data-testid="company-name-input"
                />
              </div>
            )}

            {/* Contact Person Name */}
            <div className="space-y-2">
              <Label>{newUserForm.is_corporate ? "Contact Person Name *" : "Full Name *"}</Label>
              <Input
                value={newUserForm.name}
                onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                data-testid="name-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                data-testid="email-input"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                value={newUserForm.phone}
                onChange={(e) => setNewUserForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+61 400 000 000"
                data-testid="phone-input"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Minimum 6 characters"
                data-testid="password-input"
              />
            </div>

            {/* Role selection for non-corporate */}
            {!newUserForm.is_corporate && (
              <div className="space-y-2">
                <Label>User Role</Label>
                <Select 
                  value={newUserForm.role} 
                  onValueChange={(value) => setNewUserForm(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowCreateUserDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creatingUser} data-testid="submit-create-user">
                {creatingUser ? (
                  <>Creating...</>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create {newUserForm.is_corporate ? "Corporate" : "Client"} Account
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="h-5 w-5" />
              Delete Bookings
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete <strong className="text-foreground">{selectedBookings.length} booking(s)</strong>?
            </p>
            <p className="text-sm text-red-400 mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmBulkDelete}
              disabled={bulkActionLoading}
              data-testid="confirm-delete-btn"
            >
              {bulkActionLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {selectedBookings.length} Booking(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Reset Login Credentials
            </DialogTitle>
          </DialogHeader>
          {selectedUserForReset && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-black/5 border border-black/10">
                <p className="text-sm text-muted-foreground">Resetting credentials for:</p>
                <p className="font-semibold">{selectedUserForReset.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUserForReset.email}</p>
                <Badge variant="outline" className="mt-2 capitalize">{selectedUserForReset.role}</Badge>
              </div>

              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={resetPasswordForm.newEmail}
                  onChange={(e) => setResetPasswordForm(prev => ({ ...prev, newEmail: e.target.value }))}
                  placeholder="user@example.com"
                  data-testid="reset-email-input"
                />
                <p className="text-xs text-muted-foreground">Leave unchanged to keep current email</p>
              </div>

              <div className="space-y-2">
                <Label>New Password *</Label>
                <Input
                  type="password"
                  value={resetPasswordForm.newPassword}
                  onChange={(e) => setResetPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password"
                  data-testid="reset-new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  value={resetPasswordForm.confirmPassword}
                  onChange={(e) => setResetPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                  data-testid="reset-confirm-password-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={resettingPassword}
              data-testid="submit-reset-password"
            >
              {resettingPassword ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Update Credentials
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
